import { FC, memo, useEffect, useMemo, useRef, useState } from 'react';

import { VirtualItem } from '@tanstack/react-virtual';

import { cn } from '@/lib/utils';
import { GroupBy } from '@/modules/common';
import { AssignmentModal } from '@/modules/planner/components/modals/assignment-modal/assignment-modal';

import usePlannerStore from '../../store';
import { RowItem, RowItemType } from '../../types';
import { GroupRow } from './group-row';
import { TimelineRow } from './timeline-row';

interface TimelineRowsProps {
  allRows: RowItem[];
  rows: RowItem[];
  virtualItems: VirtualItem[];
}

const ROW_ENTER_ANIMATION_MS = 300;

export const TimelineRows: FC<TimelineRowsProps> = memo(({ allRows, rows, virtualItems }) => {
  const groupBy = usePlannerStore(store => store.groupBy);
  const resetSelectedRow = usePlannerStore(store => store.resetSelectedRow);
  const selectedDates = usePlannerStore(store => store.selectedDates);
  const resetSelectedDates = usePlannerStore(store => store.resetSelectedDates);
  const selectedRow = usePlannerStore(store => store.selectedRow);

  // Track which rows are new to the data (not just toggled by collapse/expand).
  // We compare against allRows (full dataset) rather than visibleRows to avoid
  // triggering animations when rows are simply revealed by expanding a group.
  const prevAllRowKeysRef = useRef<Set<string>>(new Set());
  const [enteringKeys, setEnteringKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    const currentKeys = new Set(
      allRows.map(r => (r.parentGroupId ? `${r.parentGroupId}-${r.id}` : r.id))
    );
    const prev = prevAllRowKeysRef.current;

    // Always update the ref so the next comparison is against the latest state
    prevAllRowKeysRef.current = currentKeys;

    // Skip animation on initial load (prev is empty)
    if (prev.size > 0) {
      const entering = new Set<string>();
      for (const key of currentKeys) {
        if (!prev.has(key)) entering.add(key);
      }
      if (entering.size > 0) {
        setEnteringKeys(entering);
        const timer = setTimeout(() => setEnteringKeys(new Set()), ROW_ENTER_ANIMATION_MS);
        return () => clearTimeout(timer);
      }
    }
  }, [allRows]);

  const selectedRowData = useMemo(
    () => allRows.find(({ id }) => id === selectedRow),
    [allRows, selectedRow]
  );
  const personName = selectedRowData?.title || '';
  const userId = selectedRowData?.user?.id?.toString();
  const projectId = groupBy === GroupBy.PROJECTS ? selectedRowData?.parentGroupId : undefined;

  const onCloseAssignmentModal = () => {
    resetSelectedRow();
    resetSelectedDates();
  };

  return (
    <>
      <div className="flex flex-col">
        {virtualItems.map(virtualItem => {
          const row = rows[virtualItem.index];
          if (!row) return null;

          return (
            <div
              key={virtualItem.key}
              className="absolute left-0 top-0 w-full"
              style={{
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <div className={cn(enteringKeys.has(String(virtualItem.key)) && 'animate-row-enter')}>
                {row?.type === RowItemType.Group ? (
                  <GroupRow row={row} />
                ) : (
                  <TimelineRow row={row} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AssignmentModal
        open={!!selectedRow}
        onClose={onCloseAssignmentModal}
        initialData={{
          userName: personName,
          userId,
          projectId,
          dateRange: {
            from: selectedDates.startDate?.toDate() || new Date(),
            to: selectedDates.endDate?.toDate() || new Date(),
          },
        }}
      />
    </>
  );
});
